import React from "react";
import {
  Box,
  VStack,
  SimpleGrid,
  Text,
  Icon,
  Badge,
  Button,
  Divider,
  useColorModeValue,
} from "@chakra-ui/react";
import { MdBolt, MdStop, MdTimer, MdAttachMoney } from "react-icons/md";

const OngoingSession = ({
  sessionData,
  station,
  selectedConnector,
  handleStopCharging,
  stopLoading,
  calculateDuration,
}) => {
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textColor = useColorModeValue("gray.800", "white");
  const labelColor = useColorModeValue("gray.600", "gray.400");

  return (
    <Box
      bg={cardBg}
      borderRadius="xl"
      border="1px solid"
      borderColor={borderColor}
      p={{ base: 3, md: 6 }}
      boxShadow="sm"
    >
      <VStack align="stretch" spacing={{ base: 2, md: 4 }}>
        <Text
          fontSize={{ base: "md", md: "lg" }}
          fontWeight="bold"
          color="#FF5C00"
        >
          <Icon as={MdBolt} mr={2} />
          CHARGING SESSION
        </Text>

        <Divider />

        {/* Session Details Grid - 2 Column Layout */}
        <SimpleGrid columns={2} spacing={{ base: 2, md: 4 }}>
          {/* Left Column - Stacked Items */}
          <VStack spacing={{ base: 2, md: 3 }} align="stretch">
            {/* Status */}
            <Box
              p={{ base: 2, md: 3 }}
              bg="rgba(35, 169, 121, 0.05)"
              borderRadius="lg"
              border="1px solid"
              borderColor={borderColor}
            >
              <Text
                fontSize={{ base: "2xs", md: "xs" }}
                color={labelColor}
                mb={1}
              >
                STATUS
              </Text>
              <Badge colorScheme="brand" fontSize={{ base: "2xs", md: "xs" }}>
                {sessionData.Status || "ONGOING"}
              </Badge>
            </Box>

            {/* Mode */}
            <Box
              p={{ base: 2, md: 3 }}
              bg="rgba(35, 169, 121, 0.05)"
              borderRadius="lg"
              border="1px solid"
              borderColor={borderColor}
            >
              <Text
                fontSize={{ base: "2xs", md: "xs" }}
                color={labelColor}
                mb={1}
              >
                MODE
              </Text>
              <Text
                fontSize={{ base: "xs", md: "sm" }}
                fontWeight="bold"
                color={textColor}
              >
                {sessionData?.Mode === "units"
                  ? "UNITS"
                  : sessionData?.Mode === "time"
                  ? "TIME"
                  : sessionData?.Mode === "money"
                  ? "MONEY"
                  : "NA"}
              </Text>
            </Box>

            {/* Requested */}
            <Box
              p={{ base: 2, md: 3 }}
              bg="rgba(35, 169, 121, 0.05)"
              borderRadius="lg"
              border="1px solid"
              borderColor={borderColor}
            >
              <Text
                fontSize={{ base: "2xs", md: "xs" }}
                color={labelColor}
                mb={1}
              >
                REQUESTED
              </Text>
              <Text
                fontSize={{ base: "xs", md: "sm" }}
                fontWeight="bold"
                color={textColor}
              >
                {sessionData?.Mode === "time"
                  ? `${sessionData?.MinutesRequested || "0"} mins`
                  : sessionData?.Mode === "units" || sessionData?.Mode === "money"
                  ? `${sessionData?.UnitsRequested || "0"} kWh`
                  : "0"}
              </Text>
            </Box>

            {/* Initial Meter */}
            <Box
              p={{ base: 2, md: 3 }}
              bg="rgba(35, 169, 121, 0.05)"
              borderRadius="lg"
              border="1px solid"
              borderColor={borderColor}
            >
              <Text
                fontSize={{ base: "2xs", md: "xs" }}
                color={labelColor}
                mb={1}
              >
                Initial Meter
              </Text>
              <Text
                fontSize={{ base: "xs", md: "sm" }}
                fontWeight="bold"
                color={textColor}
              >
                {sessionData.InitialMeterValue?.toFixed(2) || "0.00"}
              </Text>
            </Box>

            {/* Power */}
            <Box
              p={{ base: 2, md: 3 }}
              bg="rgba(35, 169, 121, 0.05)"
              borderRadius="lg"
              border="1px solid"
              borderColor={borderColor}
            >
              <Text
                fontSize={{ base: "2xs", md: "xs" }}
                color={labelColor}
                mb={1}
              >
                POWER
              </Text>
              <Text
                fontSize={{ base: "xs", md: "sm" }}
                fontWeight="bold"
                color={textColor}
              >
                {sessionData.Power || station?.MaxElectricPower || "0"} kW
              </Text>
            </Box>
          </VStack>

          {/* Right Column - SOC Large Box + Stacked Smaller Items */}
          <VStack spacing={{ base: 2, md: 3 }} align="stretch">
            {/* SOC - Large Box */}
            <Box
              p={{ base: 4, md: 6 }}
              bg="rgba(35, 169, 121, 0.05)"
              borderRadius="lg"
              border="1px solid"
              borderColor={borderColor}
              display="flex"
              alignItems="center"
              justifyContent="center"
              minH={{ base: "120px", md: "150px" }}
            >
              <VStack spacing={1}>
                <Text fontSize={{ base: "xs", md: "sm" }} color={labelColor}>
                  % SOC
                </Text>
                <Text
                  fontSize={{ base: "3xl", md: "4xl" }}
                  fontWeight="bold"
                  color="#FF5C00"
                >
                  {sessionData.ConsumerSoc || "0"}%
                </Text>
              </VStack>
            </Box>

            {/* Consumption */}
            <Box
              p={{ base: 2, md: 3 }}
              bg="rgba(35, 169, 121, 0.05)"
              borderRadius="lg"
              border="1px solid"
              borderColor={borderColor}
            >
              <Text
                fontSize={{ base: "2xs", md: "xs" }}
                color={labelColor}
                mb={1}
              >
                Consumption
              </Text>
              <Text
                fontSize={{ base: "xs", md: "sm" }}
                fontWeight="bold"
                color="#FF5C00"
              >
                {sessionData.UnitsConsumed?.toFixed(2) || "0.00"} kWh
              </Text>
            </Box>

            {/* Current Meter */}
            <Box
              p={{ base: 2, md: 3 }}
              bg="rgba(35, 169, 121, 0.05)"
              borderRadius="lg"
              border="1px solid"
              borderColor={borderColor}
            >
              <Text
                fontSize={{ base: "2xs", md: "xs" }}
                color={labelColor}
                mb={1}
              >
                Current Meter
              </Text>
              <Text
                fontSize={{ base: "xs", md: "sm" }}
                fontWeight="bold"
                color={textColor}
              >
                {sessionData.CurrentMeterValue?.toFixed(2) || "0.00"}
              </Text>
            </Box>

            {/* Amount */}
            <Box
              p={{ base: 2, md: 3 }}
              bg="rgba(35, 169, 121, 0.05)"
              borderRadius="lg"
              border="1px solid"
              borderColor={borderColor}
            >
              <Text
                fontSize={{ base: "2xs", md: "xs" }}
                color={labelColor}
                mb={1}
              >
                Amount
              </Text>
              <Text
                fontSize={{ base: "xs", md: "sm" }}
                fontWeight="bold"
                color="#FF5C00"
              >
                <Icon as={MdAttachMoney} />₹
                {sessionData.TotalCostWithTax?.toFixed(2) || "0.00"}
              </Text>
            </Box>
          </VStack>
        </SimpleGrid>

        {/* Stop Button */}
        <Button
          colorScheme="red"
          size="lg"
          h={{ base: "44px", md: "48px" }}
          fontSize={{ base: "sm", md: "md" }}
          borderRadius="md"
          leftIcon={<MdStop />}
          onClick={handleStopCharging}
          isLoading={stopLoading}
          w="full"
          mt={2}
        >
          STOP 
        </Button>
      </VStack>
    </Box>
  );
};

export default OngoingSession;
